import React, { useContext, useEffect, useState, useCallback } from "react";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "./SpeechInput";

export default function EditLeadModal({ enquiry, onClose, onSaved }) {
  const { http, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [workTypes, setWorkTypes] = useState([]);
  const [works, setWorks] = useState([]);
  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    customerName: enquiry?.customerName || "",
    customerMobile: enquiry?.customerMobile || "",
    status: enquiry?.status || "IN_PROGRESS",
    assignedToUserId: enquiry?.assignedToUserId || "",
    workTypeId: enquiry?.latestWorkTypeId ? String(enquiry.latestWorkTypeId) : "",
    workId: enquiry?.latestWorkId ? String(enquiry.latestWorkId) : "",
    vehicleNo: enquiry?.vehicleNo || enquiry?.latestWork?.vehicleNo || "",
    rtoApplicationNo: enquiry?.rtoApplicationNo || enquiry?.latestWork?.rtoApplicationNo || "",
    licenseType: enquiry?.licenseType || enquiry?.latestWork?.licenseType || "",
    previousDocumentNo: enquiry?.previousDocumentNo || "",
    paymentAmount:
      enquiry?.latestPaymentAmount !== null && enquiry?.latestPaymentAmount !== undefined
        ? String(enquiry.latestPaymentAmount)
        : "",
  });

  const selectedWorkType = workTypes.find(
    (item) => String(item.id) === String(form.workTypeId)
  );
  const filteredWorks = works.filter(
    (item) => String(item.workTypeId) === String(form.workTypeId)
  );

  const loadMasters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [wtRes, wRes, uRes] = await Promise.all([
        http.get("/api/master/work-types"),
        http.get("/api/master/works"),
        http.get("/api/admin/users").catch(() => ({ data: { items: [] } })),
      ]);

      setWorkTypes(wtRes.data.items || []);
      setWorks(wRes.data.items || []);
      setUsers(uRes.data.items || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load master data.");
    } finally {
      setLoading(false);
    }
  }, [http]);

  useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  const onChange = (e) => {
    const { name, value } = e.target;

    setForm((current) => {
      if (name === "workTypeId") {
        return {
          ...current,
          workTypeId: value,
          workId: "",
          paymentAmount: "",
          vehicleNo: "",
          licenseType: "",
          previousDocumentNo: "",
        };
      }

      if (name === "workId") {
        const nextWork = works.find((item) => String(item.id) === String(value));
        return {
          ...current,
          workId: value,
          paymentAmount: nextWork ? String(nextWork.fee) : current.paymentAmount,
        };
      }

      return { ...current, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await http.put(`/api/enquiries/${enquiry.id}`, {
        customerName: form.customerName,
        customerMobile: form.customerMobile,
        status: form.status,
        assignedToUserId: form.assignedToUserId ? Number(form.assignedToUserId) : null,
        workTypeId: form.workTypeId ? Number(form.workTypeId) : null,
        workId: form.workId ? Number(form.workId) : null,
        paymentAmount: form.paymentAmount !== "" ? Number(form.paymentAmount) : null,
        vehicleNo: form.vehicleNo || null,
        rtoApplicationNo: form.rtoApplicationNo || null,
        licenseType: form.licenseType || null,
        previousDocumentNo: form.previousDocumentNo || null,
      });

      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update lead details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content shadow">
          <div className="modal-header bg-dark text-white">
            <h5 className="modal-title">
              ✏️ Edit Lead Details - {enquiry?.applicationNo}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            {error ? <div className="alert alert-danger">{error}</div> : null}

            {loading ? (
              <div className="text-center py-4 text-muted">Loading master data...</div>
            ) : (
              <form onSubmit={handleSubmit} className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">Customer Name *</label>
                  <SpeechInput
                    as="input"
                    name="customerName"
                    value={form.customerName}
                    onChange={onChange}
                    placeholder="Enter Customer Name"
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">Mobile Number *</label>
                  <input
                    type="text"
                    name="customerMobile"
                    className="form-control"
                    value={form.customerMobile}
                    onChange={onChange}
                    placeholder="Enter 10-digit mobile"
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">Work Type *</label>
                  <select
                    name="workTypeId"
                    className="form-select"
                    value={form.workTypeId}
                    onChange={onChange}
                    required
                  >
                    <option value="">Select Work Type</option>
                    {workTypes.map((wt) => (
                      <option key={wt.id} value={wt.id}>
                        {wt.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">Work Item *</label>
                  <select
                    name="workId"
                    className="form-select"
                    value={form.workId}
                    onChange={onChange}
                    required
                    disabled={!form.workTypeId}
                  >
                    <option value="">Select Work</option>
                    {filteredWorks.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name} (₹{w.fee})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedWorkType?.code === "vehicle" ? (
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">Vehicle Number *</label>
                    <SpeechInput
                      as="input"
                      name="vehicleNo"
                      value={form.vehicleNo}
                      onChange={onChange}
                      placeholder="e.g. MH12AB1234"
                      required
                    />
                  </div>
                ) : null}

                {selectedWorkType?.code === "driving_licence" ? (
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">License Type *</label>
                    <SpeechInput
                      as="input"
                      name="licenseType"
                      value={form.licenseType}
                      onChange={onChange}
                      placeholder="e.g. MCWG / LMV"
                      required
                    />
                  </div>
                ) : null}

                <div className="col-12 col-md-6">
                  <label className="form-label">RTO Application No</label>
                  <SpeechInput
                    as="input"
                    name="rtoApplicationNo"
                    value={form.rtoApplicationNo}
                    onChange={onChange}
                    placeholder="Official RTO Application No"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Previous Document No</label>
                  <SpeechInput
                    as="input"
                    name="previousDocumentNo"
                    value={form.previousDocumentNo}
                    onChange={onChange}
                    placeholder="Enter Document No"
                  />
                </div>

                {user?.role !== "team_member" ? (
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">Agreed Fee / Amount (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="paymentAmount"
                      className="form-control"
                      value={form.paymentAmount}
                      onChange={onChange}
                      placeholder="e.g. 1500"
                      required
                    />
                  </div>
                ) : null}

                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">Enquiry Status *</label>
                  <select
                    name="status"
                    className="form-select"
                    value={form.status}
                    onChange={onChange}
                  >
                    <option value="NEW">NEW</option>
                    <option value="IN_PROGRESS">IN_PROGRESS</option>
                    <option value="COMPLETED">COMPLETED</option>
                    <option value="CLOSED">CLOSED</option>
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Assigned Team Member</label>
                  <select
                    name="assignedToUserId"
                    className="form-select"
                    value={form.assignedToUserId}
                    onChange={onChange}
                  >
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.roleName})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 text-end mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary me-2"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? "Saving Changes..." : "💾 Update Lead Details"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
